import React, { useCallback } from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Tooltip } from '@mui/material';

import { ROUTES as CATALOG_ROUTES } from 'pages/Search/constants';

import useStyles from './styles';

import useCpeMatchParameters from '../../hooks/use-cpe-match-parameters';
import { CpeMatchItem as CpeMatchItemInterface } from '../../types';
import StatusTag from '../StatusTag';
import Tag from '../Tag';

interface Props {
    data: CpeMatchItemInterface;
    className?: string;
}

const CpeMatch: React.FC<Props> = ({ data, className }) => {
    const { classes, cx } = useStyles();
    const { vendor, product, version, update, edition, language, swEdition, targetSw, targetHw, vulnerable, criteria } =
        useCpeMatchParameters(data);

    const unescape = useCallback((value: string) => value.replace(/\\(.)/g, '$1').trim(), []);
    const decode = useCallback(
        (value: string) => {
            return unescape(
                value
                    .replace(/(?<!\\)[*_]+/g, ' ')
                    .replace(/\s+/, ' ')
                    .trim()
                    .toLowerCase()
            );
        },
        [unescape]
    );

    return (
        <div className={cx(classes.root, className)}>
            {vendor ? (
                <Tag
                    label={unescape(vendor)}
                    tooltip="Vendor"
                    href={CATALOG_ROUTES.VENDOR({ page: null, vendor: decode(vendor) })}
                />
            ) : null}
            {product ? (
                <Tag
                    label={unescape(product)}
                    tooltip="Product"
                    href={
                        vendor
                            ? CATALOG_ROUTES.PRODUCT_VERSIONS({
                                  page: null,
                                  vendor: decode(vendor),
                                  product: decode(product)
                              })
                            : undefined
                    }
                />
            ) : null}
            {version ? (
                typeof version === 'string' ? (
                    <>
                        <Tag label="Match" />
                        <Tag label={unescape(version)} tooltip="Version" />
                    </>
                ) : (
                    <>
                        <Tag label="Range" color="blue" tooltip="Version" />
                        {version.begin ? (
                            <Tag
                                label={unescape(version.begin.value)}
                                tooltip={`Version (${version.begin.inclusive ? 'Inclusive' : 'Exclusive'})`}
                            />
                        ) : null}
                        {version.operator}
                        {version.end ? (
                            <Tag
                                label={unescape(version.end.value)}
                                tooltip={`Version (${version.end.inclusive ? 'Inclusive' : 'Exclusive'})`}
                            />
                        ) : null}
                    </>
                )
            ) : null}
            {update ? <Tag label={unescape(update)} tooltip="Update" /> : null}
            {edition ? <Tag label={unescape(edition)} tooltip="Edition" /> : null}
            {language ? <Tag label={unescape(language)} tooltip="Language" /> : null}
            {swEdition ? <Tag label={unescape(swEdition)} tooltip="Software Edition" /> : null}
            {targetSw ? <Tag label={unescape(targetSw)} tooltip="Target Software" /> : null}
            {targetHw ? <Tag label={unescape(targetHw)} tooltip="Target Hardware" /> : null}
            {vulnerable ? (
                <StatusTag color="red" tooltip="Vulnerable" />
            ) : (
                <StatusTag color="green" tooltip="Non-vulnerable" />
            )}
            <Tooltip title={criteria}>
                <ErrorOutlineIcon color="inherit" className={classes.criteria} />
            </Tooltip>
        </div>
    );
};

export default CpeMatch;
